import React from "react";
import { Button, Card, Tag } from "antd";
import { EyeOutlined } from "@ant-design/icons";

import CurrencyFormatter from "../../../components/CurrencyFormatter";

import AddonAction from "./AddonAction";
import {
  getAddonBlockedReasonKey,
  getAddonDependencyNames,
  getAddonDescription,
  getAddonImage,
  getAddonName,
  getAddonPriceValue,
  isAddonFree,
} from "./addonPresentation";

const AddonCard = ({
  addon,
  isRtl,
  activeAddons,
  t,
  onOpenPreview,
  onReload,
  onActionLoadingChange,
}) => {
  const addonName = getAddonName(addon, isRtl);
  const addonDescription = getAddonDescription(addon, isRtl);
  const dependencyNames = getAddonDependencyNames(addon, isRtl);
  const blockedReasonKey = getAddonBlockedReasonKey(addon, activeAddons);
  const freeAddon = isAddonFree(addon);
  const priceValue = getAddonPriceValue(addon);
  const trialDays = Number(addon?.trial_validity_days) || 0;

  const truncatedDescription =
    addonDescription.length > 145
      ? `${addonDescription.substring(0, 145)}...`
      : addonDescription;

  const trialNote =
    trialDays > 0
      ? t("addon_free_trial_days", { count: trialDays, days: trialDays })
      : freeAddon
        ? t("addon_always_free")
        : t("addon_no_trial_included");

  return (
    <Card
      hoverable
      className={`addon-showcase-card ${addon?.is_subscribed ? "is-active" : ""}`}
      onClick={() => onOpenPreview(addon?.id)}
    >
      <div className="addon-showcase-card__cover">
        <img
          alt={addonName}
          src={getAddonImage(addon)}
          className="addon-showcase-card__cover-image"
        />

        <div className="addon-showcase-card__cover-gradient" />

        <div className="addon-showcase-card__cover-content">
          <Tag
            className={`addon-showcase-status ${addon?.is_subscribed ? "is-active" : "is-available"}`}
          >
            {addon?.is_subscribed ? t("active") : t("addon_available")}
          </Tag>
        </div>

        <div
          className={`addon-showcase-price-chip ${freeAddon ? "is-free" : "is-paid"}`}
        >
          {freeAddon ? (
            t("free")
          ) : (
            <>
              <CurrencyFormatter value={priceValue} /> {t("addon_per_day")}
            </>
          )}
        </div>
      </div>

      <div className="addon-showcase-card__body">
        <div className="addon-showcase-card__title">{addonName}</div>

        <div className="addon-showcase-card__description">
          {truncatedDescription}
        </div>

        {dependencyNames.length > 0 && (
          <div className="addon-showcase-pill-block">
            <div className="addon-showcase-pill-block__label">
              {t("addon_requires")}
            </div>
            <div className="addon-showcase-pill-list">
              {dependencyNames.map((name) => (
                <span className="addon-showcase-pill" key={name}>
                  {name}
                </span>
              ))}
            </div>
          </div>
        )}

        {blockedReasonKey && (
          <div className="addon-showcase-conflict">{t(blockedReasonKey)}</div>
        )}

        <div className="addon-showcase-card__footer">
          <div className="addon-showcase-card__trial-note">{trialNote}</div>

          <div
            className="addon-showcase-card__actions"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="default"
              icon={<EyeOutlined />}
              onClick={() => onOpenPreview(addon?.id)}
            >
              {t("preview")}
            </Button>

            <AddonAction
              addonData={addon}
              reloadCallback={onReload}
              buttonSize="small"
              variant="card"
              onActionLoadingChange={onActionLoadingChange}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default AddonCard;
